'use client'
import {
  DEFAULT_PAGINATION
} from "./chunk-UAMS6QZK.mjs";
import {
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/hooks/useExploreQuery.ts
import { useMemo } from "react";
import { useGeolocation, useRouter } from "@wbk/config";
import { dateAdd, endOfDay, endOfWeek, parseDate, startOfDay, startOfWeek } from "@wbk/utils";
var createParamsSource = (params) => ({
  get: (key) => {
    var _a;
    const val = params[key];
    if (Array.isArray(val)) return (_a = val[0]) != null ? _a : null;
    return val != null ? val : null;
  },
  getAll: (key) => {
    const val = params[key];
    if (Array.isArray(val)) return val;
    return val != null ? [val] : [];
  }
});
var useExploreQuery = (props) => {
  var _a;
  const predefined = props == null ? void 0 : props.predefined;
  const detectLoading = props == null ? void 0 : props.detectLoading;
  const { locale, search: searchParams, params: routeParams } = useRouter();
  const { supportedCountryCode: countryCode } = useGeolocation();
  const perPage = (props == null ? void 0 : props.perPage) || DEFAULT_PAGINATION;
  const resolvedParams = useMemo(() => {
    if (props.params) {
      return createParamsSource(props.params);
    }
    return searchParams;
  }, [props.params, searchParams]);
  const page = resolvedParams.get("page");
  const categoriesQuery = useMemo(() => {
    var _a2;
    if ((_a2 = predefined == null ? void 0 : predefined.category) == null ? void 0 : _a2.length) {
      return {
        OR: [{ category: { slug_in: predefined.category } }]
      };
    }
    const categories = resolvedParams.getAll("category");
    if (categories.length) {
      return {
        OR: [{ category: { slug_in: categories } }]
      };
    }
    return {};
  }, [predefined == null ? void 0 : predefined.category, resolvedParams]);
  const tagQuery = useMemo(() => {
    const tags = resolvedParams.getAll("tag");
    if (tags.length) {
      return {
        OR: [{ tags: { slug_in: tags } }]
      };
    }
    return {};
  }, [resolvedParams]);
  const zoneQuery = useMemo(() => {
    var _a2;
    if ((_a2 = predefined == null ? void 0 : predefined.zones) == null ? void 0 : _a2.length) {
      return {
        OR: [{ zone: { slug_in: predefined.zones } }]
      };
    }
    const zones = resolvedParams.getAll("zone") || [];
    if (zones.length) {
      return {
        OR: [{ zone: { slug_in: zones } }]
      };
    }
    return {};
  }, [predefined == null ? void 0 : predefined.zones, resolvedParams]);
  const priceQuery = useMemo(() => {
    const price = resolvedParams.get("price");
    const range = (price == null ? void 0 : price.split("_")) || [];
    const from = Number(range == null ? void 0 : range[0]);
    const to = Number(range == null ? void 0 : range[1]);
    const valid = typeof from === "number" && typeof to === "number";
    if (valid && from >= 0 && to >= from) {
      return {
        AND: [{ startingPrice_gte: from }, { startingPrice_lte: to }]
      };
    }
    return {};
  }, [resolvedParams]);
  const sortQuery = useMemo(() => {
    const sortParam = resolvedParams.get("sort");
    switch (sortParam) {
      case "latest":
        return ["sys_publishedAt_DESC", "order_ASC"];
      case "oldest":
        return ["sys_publishedAt_ASC", "order_ASC"];
      case "high_price":
        return ["startingPrice_DESC", "order_ASC"];
      case "low_price":
        return ["startingPrice_ASC", "order_ASC"];
      default:
        return ["order_ASC", "sys_publishedAt_DESC"];
    }
  }, [resolvedParams]);
  const locationQuery = useMemo(() => {
    if (predefined == null ? void 0 : predefined.locationGlobal) {
      return {};
    }
    if (predefined == null ? void 0 : predefined.countryCode) {
      return {
        OR: [
          { location: { countryCode: predefined.countryCode.toLowerCase() } },
          { location: { countryCode: predefined.countryCode.toUpperCase() } }
        ]
      };
    }
    const city = routeParams == null ? void 0 : routeParams.cityCode;
    if (city) {
      return {
        OR: [
          { location: { cityCode: city == null ? void 0 : city.toLowerCase() } },
          { location: { cityCode: city == null ? void 0 : city.toUpperCase() } }
        ]
      };
    }
    const country = (routeParams == null ? void 0 : routeParams.countryCode) || countryCode;
    const notAnywhere = country !== "anywhere";
    if (country && notAnywhere) {
      return {
        OR: [
          { location: { countryCode: country == null ? void 0 : country.toLowerCase() } },
          { location: { countryCode: country == null ? void 0 : country.toUpperCase() } }
        ]
      };
    }
    return {};
  }, [
    countryCode,
    predefined == null ? void 0 : predefined.countryCode,
    predefined == null ? void 0 : predefined.locationGlobal,
    routeParams == null ? void 0 : routeParams.cityCode,
    routeParams == null ? void 0 : routeParams.countryCode
  ]);
  const dateQuery = useMemo(() => {
    const date = resolvedParams.get("date");
    const isRange = date == null ? void 0 : date.includes("_");
    let from;
    let to;
    if (isRange) {
      const [qFrom, qTo] = (date == null ? void 0 : date.split("_")) || [];
      const safeFrom = parseDate(qFrom);
      const safeTo = parseDate(qTo);
      if (!!(safeFrom == null ? void 0 : safeFrom.getTime()) && !!(safeTo == null ? void 0 : safeTo.getTime())) {
        from = startOfDay(safeFrom);
        to = endOfDay(safeTo);
      }
    } else if (date === "today") {
      from = startOfDay(/* @__PURE__ */ new Date());
      to = endOfDay(/* @__PURE__ */ new Date());
    } else if (date === "tomorrow") {
      const tomorrowStart = dateAdd({ date: /* @__PURE__ */ new Date(), interval: "day", value: 1 });
      const tomorrowEnd = dateAdd({ date: /* @__PURE__ */ new Date(), interval: "day", value: 1 });
      if (tomorrowStart && tomorrowEnd) {
        from = startOfDay(tomorrowStart);
        to = endOfDay(tomorrowEnd);
      }
    } else if (date === "thisweek") {
      from = startOfDay(startOfWeek(/* @__PURE__ */ new Date()));
      to = endOfDay(endOfWeek(/* @__PURE__ */ new Date()));
    }
    if (from && to) {
      return {
        OR: [
          {
            schedule: {
              openDateTime_lte: to,
              closeDateTime_gte: from
            }
          },
          {
            schedule: {
              openDateTime_gte: from,
              openDateTime_lte: to
            }
          },
          {
            schedule: {
              openDateTime_lte: to,
              closeDateTime_exists: false
            }
          }
        ]
      };
    }
    return {};
  }, [resolvedParams]);
  const search = ((_a = resolvedParams.get("q")) == null ? void 0 : _a.trim()) || "";
  const query = useMemo(() => {
    const obj = null;
    if (detectLoading) {
      return obj;
    }
    const skip = Math.max(Number(page) - 1, 0);
    return {
      lang: (predefined == null ? void 0 : predefined.lang) || locale,
      limit: perPage,
      skip: skip * perPage,
      order: sortQuery,
      search: search || void 0,
      where: {
        AND: [
          __spreadValues({}, zoneQuery),
          __spreadValues({}, categoriesQuery),
          __spreadValues({}, tagQuery),
          __spreadValues({}, priceQuery),
          __spreadValues({}, dateQuery),
          __spreadValues({}, locationQuery)
        ]
      }
    };
  }, [
    detectLoading,
    predefined == null ? void 0 : predefined.lang,
    locale,
    perPage,
    page,
    sortQuery,
    zoneQuery,
    categoriesQuery,
    tagQuery,
    priceQuery,
    dateQuery,
    locationQuery,
    search
  ]);
  return {
    query,
    search,
    perPage
  };
};

export {
  useExploreQuery
};
//# sourceMappingURL=chunk-LYJH2R5V.mjs.map